"use client";

import { c as _c } from "react/compiler-runtime";
import { DocumentMenuItems } from "@/types/ui";
import { MoreVertical } from "lucide-react";
import React from "react";
import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@components/ui/sidebar";

/**
 * `DocumentBurger` is a functional component that renders a popover menu with a button trigger.
 * The menu is structured to display a list of grouped items, each containing icons and labels.
 *
 * Performance: Lazy loads menu content - only mounts when popover is actually open
 *
 * The component utilizes a state to manage its open or closed state and supports dynamic content
 * through a `data` array that defines the groups and items displayed in the menu.
 *
 * The design includes styling for the button trigger, popover content, and each menu item, ensuring
 * a consistent and visually appealing interface. Additionally, the popover aligns the content to the end
 * for proper positioning.
 *
 * Features:
 * - A ghost-style button that toggles the popover menu.
 * - Grouped menu structure with an icon and label for each item.
 * - Responsive and customizable styling.
 * - Uses the `Popover` and `Sidebar` components for layout and interaction.
 *
 * Dependencies:
 * - `React` for state management and rendering.
 * - A `data` array that contains the structure of the menu, including groups and their items.
 *
 * This component is suitable for implementing collapsible or expandable menu interfaces in web applications.
 */
export const DocumentBurger = t0 => {
  const $ = _c(7);
  const {
    items
  } = t0;
  const [isOpen, setIsOpen] = React.useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PopoverTrigger asChild={true}><Button variant="ghost" size="icon" className="h-7 w-7 data-[state=open]:bg-accent"><MoreVertical /></Button></PopoverTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isOpen || $[2] !== items) {
    t2 = isOpen && <PopoverContent className="w-56 overflow-hidden rounded-md p-0" align="end"><Sidebar collapsible="none" className="bg-transparent"><SidebarContent>{items.map(_temp2)}</SidebarContent></Sidebar></PopoverContent>;
    $[1] = isOpen;
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isOpen || $[5] !== t2) {
    t3 = <Popover open={isOpen} onOpenChange={setIsOpen}>{t1}{t2}</Popover>;
    $[4] = isOpen;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(item, index_0) {
  return <SidebarMenuItem key={index_0}>{item.trigger && <SidebarMenuButton disabled={item.isDisabled} asChild={true}>{item.trigger}</SidebarMenuButton>}{!item.trigger && <SidebarMenuButton disabled={item.isDisabled}>{item.icon && <item.icon />}<span>{item.label}</span></SidebarMenuButton>}</SidebarMenuItem>;
}
function _temp2(group, index) {
  return <SidebarGroup key={index} className="border-b last:border-none"><SidebarGroupContent className="gap-0"><SidebarMenu>{group.map(_temp)}</SidebarMenu></SidebarGroupContent></SidebarGroup>;
}